import { useEffect, useState, useCallback } from 'react';

/**
 * Theme hook. The bootstrap script in public/index.html stamps
 * <html data-theme="..."> before React mounts so dark-mode users avoid
 * a light-theme flash. This hook reads the resolved value and exposes a
 * setter that updates the DOM attribute + localStorage simultaneously.
 *
 * Theme is per-device (localStorage), not synced server-side. If we ever
 * want cross-device sync, lift it into UserSettings.
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-theme') || 'light';
  });

  // Honor OS preference changes when the user hasn't explicitly chosen.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      // Only follow the OS if the user hasn't picked a theme manually.
      if (localStorage.getItem('theme')) return;
      const next = e.matches ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', next);
      setThemeState(next);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const setTheme = useCallback((next) => {
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('theme', next); } catch { /* ignored */ }
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
};
